import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import TextHighlight from "./TextHighlight";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import { Link as RouterLink } from "react-router-dom";
import Navigation from "./Navigation";

interface User {
  id: string;
  username: string;
}

interface Message {
  id: number;
  channel: string;
  user: string;
  text: string;
  timestamp: string;
  classification: string | null;
  redact?: boolean;
}

interface MessageGroup {
  id: string;
  channel: string;
  messages: Message[];
  matching_message_ids: number[];
  timestamp: string;
}

interface Filters {
  channel: string;
  user: string;
  keyword: string;
  startDate: Date | null;
  endDate: Date | null;
}

interface SearchResponse {
  groups: MessageGroup[];
  messages: Message[];
  total: number;
  pages: number;
  current_page: number;
  has_keyword_search: boolean;
}

const MessagesView: React.FC = () => {
  // Delay initial render until after mount to avoid MUI ScrollbarSize measuring
  // before the DOM is ready, which can cause null refs. This also reduces FOUC.
  const [hydrated, setHydrated] = useState(false);
  const [channels, setChannels] = useState<string[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [filters, setFilters] = useState<Filters>({
    channel: "",
    user: "",
    keyword: "",
    startDate: null,
    endDate: null,
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [groups, setGroups] = useState<MessageGroup[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [hasKeywordSearch, setHasKeywordSearch] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setHydrated(true); }, []);

  // Fetch channels and users when `source` changes
  

  /**
   * fetchChannels: Load Slack channel list.
   *
   * Purpose: Populates the Channel filter dropdown for the Slack-only page.
   * Reason: The Slack messages view no longer supports switching sources;
   * removing that choice avoids confusing UX and accidental data mixing.
   */
  const fetchChannels = useCallback(async () => {
    try {
      const response = await fetch(`/api/channels`, {
        credentials: "include",
      });
      const data = await response.json();
      setChannels(data.channels);
    } catch (error) {
      console.error("Error fetching channels:", error);
    }
  }, []);

  /**
   * fetchUsers: Load Slack users.
   *
   * Purpose: Populates the User filter dropdown for Slack messages.
   */
  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`/api/users`, {
        credentials: "include",
      });
      const data = await response.json();
      setUsers(data.users);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }, []);

  /**
   * fetchMessages: Load paginated Slack messages for current filters.
   *
   * Purpose: Powers both grouped (keyword search) and simple message lists. The
   * function is memoized on filter fields to ensure stable references for React
   * effects, eliminating missing-deps warnings and preventing accidental
   * stale-closure bugs.
   */
  const fetchMessages = useCallback(async (currentPage: number) => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        per_page: "50",
        ...(filters.channel && { channel: filters.channel }),
        ...(filters.user && { user: filters.user }),
        ...(filters.keyword && { keyword: filters.keyword }),
        ...(filters.startDate && { start_date: filters.startDate.toISOString() }),
        ...(filters.endDate && { end_date: filters.endDate.toISOString() }),
      });
      const response = await fetch(`/api/messages?${queryParams}`, { credentials: "include" });
      const data: SearchResponse = await response.json();
      setGroups(data.groups);
      setMessages(data.messages);
      setTotalPages(data.pages);
      setHasKeywordSearch(data.has_keyword_search);
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoading(false);
    }
  }, [filters.channel, filters.user, filters.keyword, filters.startDate, filters.endDate]);

  // Fetch channels and users on mount
  useEffect(() => {
    fetchChannels();
    fetchUsers();
  }, [fetchChannels, fetchUsers]);

  // Only fetch messages when page changes (if there are already results)
  const hasAnyResults = groups.length > 0 || messages.length > 0;
  useEffect(() => {
    if (hasAnyResults) {
      fetchMessages(page);
    }
  }, [page, hasAnyResults, fetchMessages]);

  const handleSearch = () => {
    setPage(1);
    fetchMessages(1);
  };

  const handleDownload = async () => {
    try {
      const queryParams = new URLSearchParams({
        ...(filters.channel && { channel: filters.channel }),
        ...(filters.user && { user: filters.user }),
        ...(filters.keyword && { keyword: filters.keyword }),
        ...(filters.startDate && { start_date: filters.startDate.toISOString() }),
        ...(filters.endDate && { end_date: filters.endDate.toISOString() }),
      });

      const response = await fetch(`/api/messages/download?${queryParams}`, { credentials: "include" });

      if (!response.ok) {
        throw new Error("Failed to download messages");
      }

      const data = await response.json();

      // Create and download the file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      // Generate filename with current date
      const date = new Date().toISOString().split("T")[0];
      a.download = `slack-messages-${date}.json`;

      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading messages:", error);
    }
  };

  const renderGroupedMessages = () => (
    <>
      {groups.map((group) => (
        <Paper key={group.id} sx={{ p: 2, mb: 3, backgroundColor: "#f9f9f9" }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
              Channel: #{group.channel}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Context Group ({group.messages.length} messages)
            </Typography>
          </Box>
          {group.messages.map((message) => {
            const isMatching = group.matching_message_ids.includes(message.id);
            return (
              <Paper
                key={message.id}
                sx={{
                  p: 2,
                  mb: 1.5,
                  backgroundColor: "white",
                  border: isMatching ? "2px solid" : "1px solid #e0e0e0",
                  borderColor: isMatching ? "primary.main" : "#e0e0e0",
                  boxShadow: isMatching ? "0 0 8px rgba(46, 125, 50, 0.3)" : "none"
                }}
              >
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    <strong>{message.user}</strong> • {new Date(message.timestamp).toLocaleString()}
                    {isMatching && (
                      <Typography component="span" variant="body2" color="primary" sx={{ ml: 1, fontWeight: "bold" }}>
                        • MATCH
                      </Typography>
                    )}
                  </Typography>
                </Box>
                <Typography variant="body1">
                  <TextHighlight text={message.text} highlight={filters.keyword} />
                </Typography>
              </Paper>
            );
          })}
        </Paper>
      ))}
    </>
  );

  const renderSimpleMessages = () => (
    <>
      {messages.map((message) => (
        <Paper key={message.id} sx={{ p: 2, mb: 2, backgroundColor: "white" }}>
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              <strong>{message.user}</strong> • #{message.channel} • {new Date(message.timestamp).toLocaleString()}
            </Typography>
          </Box>
          <Typography variant="body1">
            {message.text}
          </Typography>
        </Paper>
      ))}
    </>
  );

  if (!hydrated) return null;

  return (
    <Box sx={{ p: 3 }}>
      <Navigation />
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center" sx={{ pb: 1 }}>
          <Grid item xs></Grid>
          <Grid item>
            <Button component={RouterLink} to="/vector" variant="outlined" size="small">
              Vector Search
            </Button>
          </Grid>
        </Grid>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Channel</InputLabel>
              <Select
                value={filters.channel}
                label="Channel"
                MenuProps={{ disablePortal: true, keepMounted: true }}
                onChange={(e) => setFilters({ ...filters, channel: e.target.value })}
              >
                <MenuItem value="">All</MenuItem>
                {channels.map((channel) => (
                  <MenuItem key={channel} value={channel}>
                    {channel}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>User</InputLabel>
              <Select
                value={filters.user}
                label="User"
                MenuProps={{ disablePortal: true, keepMounted: true }}
                onChange={(e) => {
                  setFilters({ ...filters, user: e.target.value });
                }}
              >
                <MenuItem value="">All</MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.username}>
                    {user.username}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Keyword"
              value={filters.keyword}
              onChange={(e) => setFilters({ ...filters, keyword: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <DatePicker
                  label="Start Date"
                  value={filters.startDate}
                  onChange={(date) => {
                    setFilters({ ...filters, startDate: date });
                  }}
                />
                <DatePicker
                  label="End Date"
                  value={filters.endDate}
                  onChange={(date) => {
                    setFilters({ ...filters, endDate: date });
                  }}
                />
              </Box>
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearch}
              startIcon={<SearchIcon />}
              fullWidth
              sx={{ height: "56px" }}
            >
              Search
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleDownload}
              startIcon={<DownloadIcon />}
              fullWidth
              sx={{ height: "56px" }}
            >
              Export
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {!loading && (
        <>
          {hasKeywordSearch ? renderGroupedMessages() : renderSimpleMessages()}
        </>
      )}

      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_, value) => setPage(value)}
          color="primary"
        />
      </Box>
    </Box>
  );
};

export default MessagesView;
